'use client';

import { useContext, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import CardExpandButton from '@/components/shared/card-expand-button';
import { useIsCompactTouch } from '@/hooks/use-compact-touch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Drawer, DrawerContent, DrawerTitle, DrawerTrigger } from '@/components/ui/drawer';
import { cn } from '@/lib/utils';
import {
  CarouselContext,
  type RecentWorkCardItem,
} from '@/components/sections/recent-works/carousel';
import { Badge } from '@/components/ui/badge';
import { MapPin } from 'lucide-react';
import { formatProjectType } from '@/sanity/project-data';

const ProjectDetails = dynamic(
  () => import('@/components/shared/project-details').then((mod) => mod.ProjectDetails),
  { ssr: false },
);

export interface ProjectCardProps {
  card: RecentWorkCardItem;
  index: number;
  instanceKey?: string;
  layout?: boolean;
}

export default function ProjectCard({ card, index }: ProjectCardProps) {
  const [open, setOpen] = useState(false);
  const isCompactTouch = useIsCompactTouch();
  const { onCardClose, suspendAutoplayForOverlay } = useContext(CarouselContext);

  useEffect(() => {
    if (!open) return;
    suspendAutoplayForOverlay(true);
    return () => suspendAutoplayForOverlay(false);
  }, [open, suspendAutoplayForOverlay]);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    // Avoid horizontal carousel scroll on drawer close — compounds iOS/Vaul scroll restore.
    if (!nextOpen && !isCompactTouch) onCardClose(index);
  };

  const trigger = (
    <button
      type="button"
      aria-label={`View ${card.title}`}
      className={cn(
        'group relative z-10 h-auto w-full aspect-video shrink-0 cursor-pointer touch-pan-y overflow-hidden rounded-xl bg-black text-left focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-white/40 border border-white/20 dark:border-white/10',
        'md:h-80 md:w-md xl:h-auto xl:w-full',
      )}
    >
      <Image
        src={card.src}
        alt={card.alt ?? ''}
        fill
        sizes="(max-width: 767px) calc(100vw - 2rem), (max-width: 1279px) 448px, 411px"
        className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-700 group-hover:scale-[1.02] group-hover:shadow-2xl"
      />
      <div className="pointer-events-none absolute inset-0 z-20 bg-[linear-gradient(180deg,rgba(0,0,0,0.24)_0%,rgba(0,0,0,0)_26%,rgba(0,0,0,0)_38%,rgba(0,0,0,0.82)_100%)]" />
      <Badge className="absolute top-4 left-4 z-40 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest w-fit text-white">
        {formatProjectType(card.type)}
      </Badge>
      <CardExpandButton />
      <div className="pointer-events-none absolute left-4 bottom-4 z-40">
        <h2 className="text-base sm:text-lg leading-none text-white">{card.title}</h2>
        {card.location ? (
          <div className="mt-1 flex items-center gap-1 text-xs text-white/70">
            <MapPin className="size-3 shrink-0" />
            {card.location}
          </div>
        ) : null}
      </div>
    </button>
  );

  if (isCompactTouch) {
    return (
      <Drawer open={open} onOpenChange={handleOpenChange}>
        <DrawerTrigger asChild>{trigger}</DrawerTrigger>
        <DrawerContent
          showHandle={false}
          className="h-fit max-h-[88vh] overflow-hidden border-0 p-0 shadow-none ring-0 dark:bg-neutral-950 dark:border-t dark:border-neutral-700"
        >
          <DrawerTitle className="sr-only">{card.title}</DrawerTitle>
          <ProjectDetails item={card} className="max-h-[calc(88vh-2.25rem)] overflow-y-auto" />
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-h-[88vh] overflow-hidden rounded-xl border-none bg-transparent p-0 shadow-2xl sm:max-w-[min(1120px,calc(100vw-2rem))]">
        <DialogHeader className="sr-only">
          <DialogTitle>{card.title}</DialogTitle>
          {card.location ? <DialogDescription>{card.location}</DialogDescription> : null}
        </DialogHeader>
        <ProjectDetails item={card} />
      </DialogContent>
    </Dialog>
  );
}
